import React, { useState, useRef } from "react";
import { Download } from "lucide-react";
import challanBg from "../../../Assets/challan/traffic.jpg";
import axios from "axios";

/* ---------- TABLE STYLES ---------- */
const thStyle = {
  padding: "12px",
  textAlign: "left",
  fontWeight: "600",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

const Challan = () => {
  const [searchType, setSearchType] = useState("vehicle");
  const [searchValue, setSearchValue] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [mode, setMode] = useState(null); // "manual" | "bulk"
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]); // table rows
  const [processingId, setProcessingId] = useState(null); // Store for bulk download
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  /* ---------- BULK UPLOAD ---------- */
  const handleBulkUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      // setMode("bulk");
      setResults([]);
      setError(null);
      setProcessingId(null);
    }
  };

 /* ---------- GET DETAILS ---------- */
  const handleGetDetails = async () => {
    console.log("Getting details...");
    setLoading(true);
    setError(null);

    try {
      // BULK MODE
      if (uploadedFile) {
        setMode("bulk");
        
        const formData = new FormData();
        formData.append("excelFile", uploadedFile);

        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/challan/upload`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.success) {
          setProcessingId(response.data.processingId);
          const tableRows = [];
        response.data.vehicleData?.forEach(vd => {
          if (vd.challans && vd.challans.length > 0) {
          vd.challans.forEach(c => {
            tableRows.push({
              vehicleNumber: vd.vehicleNumber || "N/A",
              accusedName: c.driver_name || "N/A",
              dlRcNumber: c.dl_no || "N/A",
              challanNo: c.challan_no || "N/A",
              state: c.challan_place || "N/A",
              challanDate: c.challan_date_time || "N/A",
              amount: c.fine_imposed || "N/A",
              status: c.challan_status || "Pending",
              chassisNumber: vd.input|| "N/A",
            });
          });
        }
      else {
    // vehicle found but no challans
    tableRows.push({
      vehicleNumber: vd.vehicleNumber || vd.input || "N/A",
      accusedName:   "—",
      dlRcNumber:    "—",
      challanNo:     "—",
      state:         "—",
      challanDate:   "—",
      amount:        "—",
      status:        vd.error ? "Error" : "No Challans",
      chassisNumber: vd.input || "N/A",
    });
  }
});
          setResults(tableRows);
          console.log("Bulk upload successful", response.data);
          setLoading(false);
        } else {
          setError(response.data.error || "Upload failed");
          setLoading(false);
        }
        return;
      }

      // MANUAL MODE
      if (!searchValue) {
        setError("Please enter a value");
        setLoading(false);
        return;
      }

      setMode("manual");

      const payload = searchType === "vehicle" 
        ? { vehicleNumber: searchValue.toUpperCase() }
        : { chassisNumber: searchValue.toUpperCase() };

      const response = await axios.post(`${API_URL}/api/challan/single`, payload);
      console.log("Response:", response.data);
      if (response.data.success) {
          setProcessingId(response.data.processingId);

        // For single vehicle, the backend downloads directly
        // But you may want to fetch and display data first
        console.log("Challan details fetched", response.data);
        const tableRows = response.data.challans.map(c => ({
        vehicleNumber: response.data.vehicleNumber || "N/A",
        accusedName: c.driver_name || "N/A",
        dlRcNumber: c.dl_no || "N/A",
        challanNo: c.challan_no || "N/A",
        state: c.challan_place || "N/A",
        challanDate: c.challan_date_time || "N/A",
        amount: c.fine_imposed || "N/A",
        status: c.challan_status || "Pending",
        chassisNumber: response.data.chassisNumber || "N/A",
      }));
      
      setResults(tableRows);
      } else {
        setError(response.data.error || "Failed to fetch details");
        setResults([]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error:", err);
      setError(err.response?.data?.error || err.message || "Something went wrong");
      setLoading(false);
    }
  };


  /* ---------- DOWNLOAD ---------- */
  const handleDownload = async () => {
    try{
    if (mode === "bulk") {
      if (!processingId) {
          setError("No processing ID. Please upload and process first.");
          return;
      }
       // Download bulk ZIP
      const response = await axios.get(
          `${API_URL}/api/challan/download/${processingId}`,
          {
            responseType: "blob",
          }
      );
      // Create blob and download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `challans_${processingId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

      console.log("Download bulk processed file");
    } else if (mode === "manual") {
      const response = await axios.get(
    `${API_URL}/api/challan/download/${processingId}`,
    { responseType: "blob" }
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `vehicle_${searchValue}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
}
    } catch (err) {
      console.error("Download error:", err);
      setError(err.response?.data?.error || err.message || "Download failed");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        overflow: "hidden",
      }}
    >
      {/* Background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${challanBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(4px) brightness(0.75)",
          transform: "scale(1.05)",
          zIndex: 0,
        }}
      />

      {/* CARD */}
      <div
        style={{
          width: "100%",
          maxWidth: "900px",
          background: "rgba(235,238,242,0.96)",
          borderRadius: "16px",
          padding: "40px",
          marginTop: "40px",
          boxShadow: "0 15px 40px rgba(0,0,0,0.3)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "30px" , fontSize : "30px" , fontWeight: "500",}}>
          Check Pending Challans
        </h2>

        {/* SEARCH TYPE */}
        {/* Search Type Options */}
<div
  style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "60px",
    marginBottom: "30px",
  }}
>
  {[
    // { label: "Challan Number", value: "challan" },
    { label: "Vehicle Number", value: "vehicle" },
    { label: "Chassis Number", value: "chassis" },
  ].map((item) => (
    <label
      key={item.value}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        cursor: "pointer",
        fontSize: "16px",
        fontWeight: searchType === item.value ? "600" : "500",
        color: searchType === item.value ? "#1aa39a" : "#333",
      }}
    >
      <input
        type="radio"
        name="searchType"
        checked={searchType === item.value}
        onChange={() => {
          setSearchType(item.value);
          setSearchValue("");
        }}
        style={{
          accentColor: "#1aa39a",
          width: "16px",
          height: "16px",
        }}
      />
      {item.label}
    </label>
  ))}
</div>


        {/* INPUT */}
        <input
          type="text"
          placeholder={`Enter ${searchType} number`}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
          style={{ width: "100%", height: "40px", marginBottom: "20px" }}
        />

        {/* FILE INPUT */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv,.xlsx,.xls,.pdf"
          style={{ display: "none" }}
        />

        {/* BUTTONS */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
          }}
        >
          <div>
            <button
              onClick={handleBulkUpload}
              style={{
                background: "#2196f3",
                color: "#fff",
                padding: "10px 22px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              Bulk Upload
            </button>

            {uploadedFile && (
              <div style={{ marginTop: "6px", fontSize: "14px" }}>
                📄 {uploadedFile.name}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={handleGetDetails}
              disabled={loading}
              style={{
                background: "#2196f3",
                color: "#fff",
                padding: "10px 22px",
                borderRadius: "6px",
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "Getting details..." : "GET DETAILS"}
            </button>

            <button
              onClick={handleDownload}
              style={{
                background: "#4caf50",
                color: "#fff",
                padding: "10px 22px",
                borderRadius: "6px",
                border: "none", 
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor:
                !processingId && mode !== "manual" ? "not-allowed" : "pointer",
                opacity: !processingId && mode !== "manual" ? 0.6 : 1,
              }}
            >
              <Download size={16} /> DOWNLOAD
            </button>
          </div>
        </div>
      </div>

      {/* TABLE */}
      {(mode === "manual"|| mode === "bulk") && !loading && (
        <div
          style={{
            width: "100%",
            maxWidth: "1200px",
            marginTop: "48px",
            background: "#fff",
            borderRadius: "8px",
            overflowX: "auto",
            zIndex: 1,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#3b82f6", color: "#fff" }}>
              <tr>
                <th style={thStyle}>Vehicle Number</th>
                <th style={thStyle}>Accused Name</th>
                <th style={thStyle}>DL / RC Number</th>
                <th style={thStyle}>Challan No.</th>
                <th style={thStyle}>State</th>
                <th style={thStyle}>Challan Date</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Chassis Number</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                    Challan Not Found
                  </td>
                </tr>
              ) : (
                results.map((row, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>{row.vehicleNumber}</td>
                    <td style={tdStyle}>{row.accusedName}</td>
                    <td style={tdStyle}>{row.dlRcNumber}</td>
                    <td style={tdStyle}>{row.challanNo}</td>
                    <td style={tdStyle}>{row.state}</td>
                    <td style={tdStyle}>{row.challanDate}</td>
                    <td style={tdStyle}>{row.amount}</td>
                    <td style={tdStyle}>{row.status}</td>
                    <td style={tdStyle}>{row.chassisNumber}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Challan;
